// @flow

import Service from './service';
import type { MemberType } from '../types/member';
import type { IfpAppType, AppType } from '../types/ifp-app';
import type { IfpCancelableType } from '../types/ifp-cancelable';
import type { IfpCrossSellType } from '../types/ifp-cross-sell';
import type { HttpResponseType } from '../types/http-response';

type PostPlanArgumentsType = {
  plan: Object,
  premium: number,
  plan_id: number,
  carrier_id: number,
  master_plan_id?: number,
};

type IfpCrossSellPostType = {
  source_app_id: string,
  target_app_type: string,
  context: string,
  shared_resources: Array<string>,
};

type IsValidToSubmitType = {
  ifp_app: IfpAppType,
  isValid: boolean,
};

type ActiveAppOptionsType = {
  types: Array<AppType>,
}

export default class IfpAppService extends Service {
  query(options: Object = {}): Promise<Array<IfpAppType>> {
    return this.api.get(`${this.baseUrl}/application`, options)
      .then((res: HttpResponseType) => (res.data: Array<IfpAppType>));
  }

  get(app_id: string, options: Object = {}): Promise<IfpAppType> {
    return this.api.get(`${this.baseUrl}/application/${app_id}`, options)
      .then((res: HttpResponseType) => (res.data: IfpAppType));
  }

  post(ifp_app: IfpAppType): Promise<IfpAppType> {
    return this.api.post(`${this.baseUrl}/application`, ifp_app)
      .then((res: HttpResponseType) => (res.data: IfpAppType));
  }

  put(app_id: string, ifp_app: IfpAppType): Promise<IfpAppType> {
    return this.api.put(`${this.baseUrl}/application/${app_id}`, ifp_app)
      .then((res: HttpResponseType) => (res.data: IfpAppType));
  }

  postPlan(app_id: string, options: PostPlanArgumentsType): Promise<IfpAppType> {
    return this.api.post(`${this.baseUrl}/application/${app_id}/plan`, options)
      .then((res: HttpResponseType) => (res.data: IfpAppType));
  }

  reset(app_id: string): Promise<IfpAppType> {
    return this.api.post(`${this.baseUrl}/application/${app_id}/reset`, {})
      .then((res: HttpResponseType) => (res.data: IfpAppType));
  }

  copy(app_id: string): Promise<IfpAppType> {
    return this.api.post(`${this.baseUrl}/application/${app_id}/copy`, {})
      .then((res: HttpResponseType) => (res.data: IfpAppType));
  }

  saveMembers(members: Array<MemberType> = [], options: Object = {}) {
    const { app_id } = options;
    if (!members.length) {
      return Promise.reject({ err: 'No members supplied' });
    }
    if (!app_id) {
      return Promise.reject({ err: 'No app supplied' });
    }
    return this.api.post(`${this.baseUrl}/application/${app_id}/member`, { members })
      .then((res: HttpResponseType) => ({ members: (res.data: Array<MemberType>) }));
  }

  appCancel(app_id: string, options: Object = {}): Promise<IfpCancelableType> {
    return this.api.get(`${this.baseUrl}/application/cancel/${app_id}`, options)
      .then((res: HttpResponseType) => (res.data: IfpCancelableType));
  }

  getCrossSellSource(target_app_id: string, options: Object = {}): Promise<IfpCrossSellType|null> {
    return this.api.get(`${this.baseUrl}/application/cross-sell/source/${target_app_id}`, options)
      .then((res: HttpResponseType) => (res.data ? res.data.ifp_cross_sell : null));
  }

  postCrossSell(options: IfpCrossSellPostType): Promise<IfpCrossSellType|null> {
    return this.api.post(`${this.baseUrl}/application/cross-sell`, options)
      .then((res: HttpResponseType) => (res.data ? res.data.ifp_cross_sell : null));
  }

  isValidToSubmit(app_id: string, effectiveDate: Date): Promise<IsValidToSubmitType> {
    const now = new Date();
    const params = {
      app_id,
      effectiveDate,
      m: now.getTimezoneOffset(),
    };
    return this.api.post(`${this.baseUrl}/health/application/is-valid-to-submit`, params)
      .then((res: HttpResponseType) => res.data);
  }

  getActiveApps(options: ActiveAppOptionsType): Promise<Array<IfpAppType>> {
    return this.api
      .get(`${this.baseUrl}/application/active/app`, options)
      .then((res: HttpResponseType) => (res.data: Array<IfpAppType>));
  }
}
